import { AppRuleActions, AppRuleNames } from "@Front/Api/Entities/rule";
import { Routes } from "@Front/Config/Routes";
import { COMMON_UI_I18N } from "@Front/Components/DesignSystem/helpers/commonUiI18n";
import {
	AdjustmentsVerticalIcon,
	ArrowsRightLeftIcon,
	BanknotesIcon,
	IdentificationIcon,
	Square3Stack3DIcon,
	TagIcon,
	UserGroupIcon,
	UsersIcon,
} from "@heroicons/react/24/outline";
import { IItem } from "@Front/Components/DesignSystem/Menu/MenuItem";

type CreateOfficeItemParams = {
	icon: React.ReactNode;
	text: string;
	link: string;
	routesActive: string[];
	ruleName?: AppRuleNames;
	rules?: Array<{ action: AppRuleActions; name: AppRuleNames }>;
};

const MENU_LABELS = COMMON_UI_I18N.navigationMenu;

function createOfficeItem(params: CreateOfficeItemParams): IItem {
	const { icon, text, link, routesActive, ruleName, rules } = params;
	const resolvedRules =
		rules ?? (ruleName ? [{ action: AppRuleActions.update, name: ruleName }] : []);
	return {
		icon,
		text,
		link,
		routesActive,
		rules: resolvedRules,
	};
}

const OFFICE_ITEMS_PARAMS: CreateOfficeItemParams[] = [
	{
		icon: <UsersIcon />,
		text: MENU_LABELS.office.collaborators,
		link: Routes.pages.Collaborators.base,
		routesActive: [Routes.pages.Collaborators.CollaboratorInformations, Routes.pages.Collaborators.base],
		ruleName: AppRuleNames.users,
	},
	{
		icon: <UserGroupIcon />,
		text: MENU_LABELS.office.clientsBase,
		link: Routes.pages.OfficeDirectory.clients,
		routesActive: [Routes.pages.OfficeDirectory.clients],
		rules: [{ action: AppRuleActions.read, name: AppRuleNames.officeFolders }],
	},
	{
		icon: <IdentificationIcon />,
		text: MENU_LABELS.office.thirdPartiesBase,
		link: Routes.pages.OfficeDirectory.thirdParties,
		routesActive: [Routes.pages.OfficeDirectory.thirdParties],
		rules: [{ action: AppRuleActions.read, name: AppRuleNames.officeFolders }],
	},
	{
		icon: <ArrowsRightLeftIcon />,
		text: MENU_LABELS.office.confreresBase,
		link: Routes.pages.OfficeDirectory.confreres,
		routesActive: [Routes.pages.OfficeDirectory.confreres],
		rules: [{ action: AppRuleActions.read, name: AppRuleNames.officeFolders }],
	},
	{
		icon: <AdjustmentsVerticalIcon />,
		text: MENU_LABELS.office.rolesManagement,
		link: Routes.pages.Roles.base,
		routesActive: [Routes.pages.Roles.Create, Routes.pages.Roles.RolesInformations, Routes.pages.Roles.base],
		ruleName: AppRuleNames.officeRoles,
	},
	{
		icon: <Square3Stack3DIcon />,
		text: MENU_LABELS.office.deedTypesSettings,
		link: Routes.pages.DeedTypes.base,
		routesActive: [
			Routes.pages.DeedTypes.Create,
			Routes.pages.DeedTypes.Edit,
			Routes.pages.DeedTypes.base,
			Routes.pages.DocumentTypes.base,
			Routes.pages.DocumentTypes.Create,
			Routes.pages.DocumentTypes.Edit,
			Routes.pages.DocumentTypes.DocumentTypesInformations,
		],
		ruleName: AppRuleNames.deedTypes,
	},
	{
		icon: <BanknotesIcon />,
		text: MENU_LABELS.office.officeRib,
		link: Routes.pages.OfficesRib,
		routesActive: [Routes.pages.OfficesRib],
		ruleName: AppRuleNames.rib,
	},
	{
		icon: <TagIcon />,
		text: MENU_LABELS.office.subscription,
		link: Routes.pages.Subscription.Manage.base,
		routesActive: [
			Routes.pages.Subscription.Error,
			Routes.pages.Subscription.Success,
			Routes.pages.Subscription.Invite,
			Routes.pages.Subscription.Manage.base,
			Routes.pages.Subscription.ManageCollaborators,
			Routes.pages.Subscription.New,
			Routes.pages.Subscription.Subscribe.base,
		],
	},
];

export function getOfficeItems(): IItem[] {
	return OFFICE_ITEMS_PARAMS.map((p) => createOfficeItem(p));
}
